/**
 * Hook for acquiring an access token against the configured API scope.
 *
 * Silent acquisition only. When the directory refuses a silent renewal
 * (the account was disabled, the password changed, Conditional Access now
 * demands more), the answer is **not** a popup that would let the person
 * carry on: the session is ended on the server with reason
 * `renewal_failed` (FR.2092, ADR-0046) and they are sent to sign in again.
 * The frontend never sees a refresh token — that's MSAL's internal cache
 * only (the tab's sessionStorage, ADR-0053). The latest access token is remembered
 * in memory so that sign-out can name the session to end.
 */

import { useCallback } from "react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { useAuthConfig } from "./useAuthConfig";
import { endSession, loginPathFor, rememberToken } from "./session";

export function useAccessToken(): () => Promise<string> {
  const { instance, accounts } = useMsal();
  const config = useAuthConfig();

  return useCallback(async () => {
    const account = accounts[0];
    if (!account) {
      throw new Error("Not signed in.");
    }
    try {
      const result = await instance.acquireTokenSilent({
        account,
        scopes: config.scopes,
      });
      rememberToken(result.accessToken);
      return result.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        await endSession(instance, "renewal_failed");
        window.location.assign(loginPathFor("renewal_failed"));
        throw new Error("Your sign-in could not be renewed. Please sign in again.");
      }
      throw err;
    }
  }, [instance, accounts, config.scopes]);
}
